import React, { useState } from "react";
import { ShieldCheck, Loader2, Save, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";

export default function AdminAccount() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    current_password: "",
    new_username: "",
    new_password: "",
    confirm_password: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast({ title: "Enter your current password", variant: "destructive" });
      return;
    }
    if (!form.new_username && !form.new_password) {
      toast({ title: "Nothing to change", description: "Enter a new username and/or password.", variant: "destructive" });
      return;
    }
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/auth/credentials", {
        current_password: form.current_password,
        new_username: form.new_username || null,
        new_password: form.new_password || null,
      });
      // refresh token so the session stays valid (esp. if username changed)
      localStorage.setItem("kiki_token", res.data.access_token);
      toast({ title: "Credentials updated", description: "Use your new login details next time you sign in." });
      setForm({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast({
        title: "Update failed",
        description: err?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#0B2A6B] text-yellow-400 flex items-center justify-center">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0B2A6B]">Account &amp; Password</h1>
          <p className="text-sm text-slate-500">
            Signed in as <span className="font-semibold text-slate-700">{user?.username}</span>. Change your admin username or password below.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-5" data-testid="account-form">
        <div>
          <Label className="text-sm">Current password *</Label>
          <div className="relative mt-1.5">
            <Input
              type={show ? "text" : "password"}
              value={form.current_password}
              onChange={(e) => update("current_password", e.target.value)}
              placeholder="Enter current password"
              data-testid="account-current-password"
              autoComplete="current-password"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5 space-y-5">
          <div>
            <Label className="text-sm">New username</Label>
            <Input
              className="mt-1.5"
              value={form.new_username}
              onChange={(e) => update("new_username", e.target.value)}
              placeholder={user?.username || "New username"}
              data-testid="account-new-username"
              autoComplete="username"
            />
            <p className="text-xs text-slate-400 mt-1">Leave blank to keep your current username.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">New password</Label>
              <Input
                className="mt-1.5"
                type={show ? "text" : "password"}
                value={form.new_password}
                onChange={(e) => update("new_password", e.target.value)}
                placeholder="Min 6 characters"
                data-testid="account-new-password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-sm">Confirm new password</Label>
              <Input
                className="mt-1.5"
                type={show ? "text" : "password"}
                value={form.confirm_password}
                onChange={(e) => update("confirm_password", e.target.value)}
                placeholder="Re-enter new password"
                data-testid="account-confirm-password"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Button type="submit" disabled={saving} className="bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold" data-testid="account-save-btn">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Updating...</> : <><Save className="h-4 w-4 mr-2" />Update Credentials</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
